import React, { useState, useEffect, useCallback } from "react";
import { Grid } from "antd";
import DesktopNav from "./components/DesktopNav";
import MobileNav from "./components/MobileNav";
import { staticDebugMenuData, ctaButtons } from "./components/menuData";
import parseSitemegamenuToMenuData from "./components/transformMenuData";

// DEBUG MODE - Set to true to keep submenu always open for development
const DEBUG_MODE = false; // Change to true when styling/developing submenu

const Nav2025 = (props) => {
  const { homeurl, search, logo_white, logo_colored, languages = [] } = props;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { xl } = screens;

  // Resolve menu data: prefer props.sitemegamenu when available
  const resolvedMenuData = React.useMemo(() => {
    try {
      if (props && props.sitemegamenu) {
        const parsed = parseSitemegamenuToMenuData(props.sitemegamenu);
        if (parsed && parsed.length) return parsed;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "Failed parsing sitemegamenu, falling back to static menuData in DEBUG mode.",
        e
      );
    }
    // Show static menu in debug mode for easier development if parsing fails
    return DEBUG_MODE ? staticDebugMenuData : [];
  }, [props]);

  // In debug mode, start with first submenu item active from resolved data
  const firstSubmenuItem = DEBUG_MODE
    ? resolvedMenuData.find((item) => item.hasSubmenu)
    : null;
  const [activeMenu, setActiveMenu] = useState(firstSubmenuItem);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      setActiveMenu(null);
      setMobileMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Check if we're scrolled past threshold
      const isScrolledPastThreshold = currentScrollY > 0;
      // Update scroll state
      setIsScrolled(isScrolledPastThreshold);

      // Hide/show header based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past 100px
        setIsVisible(false);
        // Also close submenu when scrolling down
        setActiveMenu(null);
      } else {
        // Scrolling up or at top
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Apply visibility to parent header element
  useEffect(() => {
    const headerElement = document.querySelector(".site-header, header");
    if (headerElement) {
      // Add transition styles if not already present
      if (!headerElement.style.transition) {
        headerElement.style.transition = "opacity 0.3s ease-in-out";
      }

      // Apply visibility
      if (isVisible) {
        headerElement.style.opacity = "1";
        headerElement.style.pointerEvents = "auto";
      } else {
        headerElement.style.opacity = "0";
        headerElement.style.pointerEvents = "none";
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const homeUrl = homeurl || "/";
  const searchIcon = search || "/dist/images/icon-search.svg";

  return (
    <nav className="hardcoded-nav z-50">
      {xl ? (
        <DesktopNav
          menuData={resolvedMenuData}
          ctaButtons={ctaButtons}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          homeUrl={homeUrl}
          searchIcon={searchIcon}
          isScrolled={isScrolled}
          debugMode={DEBUG_MODE}
          logoWhite={logo_white}
          logoColored={logo_colored}
          languages={languages}
        />
      ) : (
        <MobileNav
          menuData={resolvedMenuData}
          ctaButtons={ctaButtons}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          homeUrl={homeUrl}
          searchIcon={searchIcon}
          isScrolled={isScrolled}
          languages={languages}
        />
      )}
    </nav>
  );
};

export default Nav2025;
